/**
 * Name and team normalization shared by every source we merge onto the board
 * (Sleeper draft picks, the ECR and ADP feeds, the nflverse context feed).
 *
 * Kept free of imports so it can be exercised directly in Node — the matching
 * rules here are the difference between a drafted player leaving the board and
 * silently sitting on it.
 *
 * These rules mirror `model/ffmodel/match.py`. They have to: the context feed
 * is exported already keyed, so any disagreement between the two sides is a
 * column of blanks for the players it affects and no error anywhere.
 */

/** Generational suffixes, stripped only as whole trailing tokens. */
const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);

/**
 * Nicknames that share no prefix with the rostered name, so no general rule
 * will ever catch them.
 */
const NAME_ALIASES = {
  'hollywood brown': 'marquise brown',
  'chig okonkwo': 'chigoziem okonkwo',
  'scotty miller': 'scott miller',
  'cadillac williams': 'carnell williams',
  // Surname changed on the roster feed but not on draft boards.
  'nyheim hines': 'nyheim millerhines',
};

/**
 * Lowercase, strip accents and punctuation, drop trailing suffixes.
 *
 * Suffixes go as whole tokens, never as bare substrings. Stripping them as
 * substrings is what an earlier version did, and it quietly ate the letters
 * out of any name that merely contained one — "Iverson" became "erson" and
 * matched nothing on either side.
 */
export const normalizeNameKey = (name) => {
  const text = (name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const tokens = text ? text.split(' ') : [];
  while (tokens.length > 2 && SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop();

  const key = tokens.join(' ');
  return NAME_ALIASES[key] || key;
};

/**
 * KeepTradeCut and Sleeper spell several teams differently (KTC uses three
 * letters everywhere: LVR/SFO/NEP/KCC/GBP/TBB/NOS/JAC). Left unmapped, every
 * player on those eight teams silently fails to match and their picks never
 * come off the board — so both sides get folded onto one canonical code.
 */
const TEAM_ALIASES = {
  GBP: 'GB',
  JAC: 'JAX',
  JAG: 'JAX',
  KCC: 'KC',
  LVR: 'LV',
  OAK: 'LV',
  NEP: 'NE',
  NOS: 'NO',
  SFO: 'SF',
  TBB: 'TB',
  WSH: 'WAS',
  ARZ: 'ARI',
  AZ: 'ARI',
  BLT: 'BAL',
  CLV: 'CLE',
  HST: 'HOU',
  LA: 'LAR',
  STL: 'LAR',
  SD: 'LAC',
  SDC: 'LAC',
};

export const canonicalTeam = (team) => {
  const code = (team || '').toUpperCase().trim();
  if (!code || code === 'FA') return '';
  return TEAM_ALIASES[code] || code;
};

export const teamRoughMatch = (sleeperTeam, boardTeam) => {
  const a = canonicalTeam(sleeperTeam);
  const b = canonicalTeam(boardTeam);
  // A missing team on either side (free agent, unsigned) should not veto a match.
  if (!a || !b) return true;
  if (a === b) return true;
  // Tolerate a future rename that only differs by a trailing letter (SF/SFO).
  return a.startsWith(b) || b.startsWith(a);
};

/**
 * The identity a row is looked up by.
 *
 * Defenses are keyed on their team and nothing else. Every feed spells them
 * differently — FantasyPros says "Seattle Seahawks", Fantasy Football
 * Calculator and our own export say "Seattle Defense" — and no name rule
 * bridges those, so all 32 of them silently fell off any column joined by
 * name. The team code is the one thing all three agree on.
 */
export const playerKey = (name, position, team) =>
  position === 'DST'
    ? `DST|${canonicalTeam(team)}`
    : `${normalizeNameKey(name)}|${position}|${canonicalTeam(team)}`;

/**
 * Index a feed for lookup by identity, then by name and position, then by
 * name alone.
 *
 * The looser passes are not sloppiness: a player traded since the feed was
 * built has the wrong team on one side, and a gadget back listed RB by one
 * source and WR by another is common enough to matter. An ambiguous name —
 * two active players who normalize the same — matches neither, which is the
 * right answer when the alternative is attaching one man's age to another.
 */
export const indexPlayers = (players) => {
  const byKey = new Map();
  const byNamePos = new Map();
  const byName = new Map();

  for (const p of players || []) {
    const key = playerKey(p.name, p.position, p.team);
    if (!byKey.has(key)) byKey.set(key, p);

    if (p.position === 'DST') continue;
    const nameKey = normalizeNameKey(p.name);
    const namePos = `${nameKey}|${p.position}`;
    if (!byNamePos.has(namePos)) byNamePos.set(namePos, []);
    byNamePos.get(namePos).push(p);
    if (!byName.has(nameKey)) byName.set(nameKey, []);
    byName.get(nameKey).push(p);
  }

  return (row) => {
    if (!row?.name && row?.position !== 'DST') return null;
    const exact = byKey.get(playerKey(row.name, row.position, row.team));
    if (exact) return exact;
    if (row.position === 'DST') return null;

    const nameKey = normalizeNameKey(row.name);
    const samePosition = byNamePos.get(`${nameKey}|${row.position}`) || [];
    if (samePosition.length === 1) return samePosition[0];

    const sameName = byName.get(nameKey) || [];
    return sameName.length === 1 ? sameName[0] : null;
  };
};

/**
 * The id a board row is remembered by.
 *
 * Watchlists and the drafted set live in session storage across reloads, so
 * this has to stay stable while a player's team does not. Name and position
 * do that; a defense's name does not — three feeds spell it three ways — so
 * defenses are identified by their team, which is the thing that cannot
 * change.
 */
export const boardRowId = (name, position, team) =>
  position === 'DST'
    ? `dst|${canonicalTeam(team)}`
    : `${normalizeNameKey(name)}|${position}`;
